import type {
  EnemyConfig,
  EnemySpecial,
  Element,
  EnemyArchetype,
} from "./types.ts";
import type { Vec2 } from "../core/types.ts";
import { SHIELD, AURA } from "../core/config.ts";
import { ENEMY_BEHAVIORS } from "../systems/EnemyBehaviors.ts";

/** BL-36 render-only reaction timings (ms). The hit flash + squash lasts ~2
 *  frames (§6.8 "1–2-frame emissive flash"); the spawn scale-in is a short
 *  intro. Decayed by dt in {@link Enemy.update}. */
const HIT_FLASH_MS = 150;
const SPAWN_MS = 200;

export interface ContagionState {
  duration: number;
  spreadRadius: number;
  spreadChance: number;
  maxGenerations: number;
  spreadInterval: number;
  spreadTimer: number;
  speedDebuff: number;
  /** BL-175 / D-266 (S170) — virus's GOLD "PANDEMIC · Patient Zero" tag + spore
   *  reach (px). Set only when a GOLD virus tower seeds this infection
   *  (`infectEnemiesInRadius`); when this enemy DIES the WaveManager death scan
   *  bursts a deterministic spore cloud re-infecting every foe within
   *  `goldSporeRadius` (the plague self-sustains). Absent ⇒ no burst (byte-safe:
   *  no fixture infects from a GOLD tower → no contagion is ever gold-tagged). */
  goldSporeRadius?: number;
}

export interface DotState {
  damage: number;
  timer: number;
  tickTimer: number;
  tickInterval: number;
  generation: number;
  contagion?: ContagionState;
}

export class Enemy {
  x = 0;
  y = 0;
  hp = 0;
  maxHp = 0;
  speed = 0;
  baseSpeed = 0;
  /** Immutable post-init reference for idempotent global-speed multipliers.
   *  baseSpeed = originalBaseSpeed × CombatSystem.globalSpeedMult. */
  originalBaseSpeed = 0;
  reward = 0;
  color = "#fff";
  radius = 8;
  type = "";
  /** BL-51: procedural silhouette family, dispatched by EnemyRenderer.
   *  Render-only — the sim never reads it. Defaults to the diamond fallback. */
  shape = "diamond";
  /** GA-07 / GA-19 — the authored combat archetype; `isTanky` reads this.
   *  Defaults to "standard" so a pooled reuse can never leak the previous
   *  occupant's archetype (the pool reuses instances via init). */
  archetype: EnemyArchetype = "standard";
  weakness: Element | null = null;
  resistance: Element | null = null;
  active = false;

  // Path tracking
  path: Vec2[] | null = null;
  pathIndex = 0;
  /** B7 stuck-watchdog (Spec `2026-time-warp-softlock`): sim-ms this enemy has
   *  made NO progress — neither advancing `pathIndex` nor moving in x/y. Reset
   *  on any progress. Reset to 0 by `init` so a pooled reuse starts clean. */
  stalledMs = 0;
  /** Last observed progress signature, for the watchdog above. */
  lastProgressIndex = -1;
  lastProgressX = Number.NaN;
  lastProgressY = Number.NaN;
  reachedEnd = false;
  /** CR-H6 (LD-22.1-4): which PathRegistry path this enemy spawned on.
   *  "primary" for normal spawn, "breach:col,row" for breach-activated
   *  secondary spawns. Used by rerouteActiveEnemies to update only the
   *  matching enemies when its path changes. */
  spawnPathId: string = "primary";

  // Anti-cheese: tracks tiles visited, speeds up on revisits (caps +50%)
  private visitedTiles = new Set<string>();
  private pathSpeedBonus = 0;

  // Special abilities
  special: EnemySpecial | null = null;
  /** S39 — the shiftResistance cycle timer (ms). Public so the relocated
   *  ENEMY_BEHAVIORS.shiftResistance handler (systems) advances it (BL-151). */
  shiftTimer = 0;
  addSpawned = false;
  /** BL-155 (S158) — the Leech siphon timer (ms accumulator) + the cumulative
   *  credits drained this instance (the `drainCap` bound). Public so the relocated
   *  `ENEMY_BEHAVIORS.leech` onPostUpdate handler advances them (BL-151 seam). */
  leechTimer = 0;
  leechDrained = 0;

  /** Lives subtracted from mainframe when this enemy leaks (default 1) */
  livesDamage = 1;
  /** Stealth: invisible to towers unless revealed by Signal */
  stealth = false;
  /** Whether currently revealed by a Signal tower */
  revealed = false;
  /** Timestamp (seconds) when reveal state last changed — drives transition VFX */
  revealedAt = 0;
  /** Damage dealt to firewalls per second when adjacent */
  firewallDamage = 0;
  /** Interaction immunities (e.g. "chain"), read via isImmuneTo() */
  immunities: string[] = [];

  // Shield: wave-15+ extra HP pool, absorbs damage before base HP (S60)
  shieldHp = 0;
  shieldMax = 0;

  /** BL-152 (S154) — the Overseer aura buff stamp (ms). A living Act-III Overseer
   *  within range re-stamps this to AURA.WINDOW_MS each tick; `updateStatusEffects`
   *  decays it and, while > 0, `applyDamage` mitigates incoming damage by
   *  AURA.DAMAGE_TAKEN_MULT. 0 ⇒ unbuffed (the byte-identical default — every
   *  enemy before any Overseer exists reads exactly 0; the host's death stops the
   *  refresh, so the buff decays to 0 → the aura collapses, the named counter-play). */
  auraBuffTimer = 0;
  /** S156 / PT-41 — the aura mitigation STRENGTH, stamped by the Overseer
   *  alongside `auraBuffTimer` (its `special.damageTakenMult`, the lifted-to-data
   *  AURA strength). Read by `applyDamage` only while `auraBuffTimer > 0`, so the
   *  default (`AURA.DAMAGE_TAKEN_MULT`) is inert for every unbuffed enemy →
   *  byte-identical. Defaulting to the constant keeps a manually-buffed enemy
   *  (unit tests that set the timer without a host stamp) mitigating by the
   *  config value. */
  auraDamageMult: number = AURA.DAMAGE_TAKEN_MULT;

  // Status effects
  dots: Map<Element, DotState> = new Map();
  slowMultiplier = 1;
  slowTimer = 0;

  // Mark (SIGNAL tower debuff)
  marked = false;
  markTimer = 0;
  markDamageBonus = 0;

  // PID_HIJACK: hijacked enemy moves backward, damages others, then crashes.
  // Timer in ms; when > 0 the enemy is hijacked. hijackedDps = damage/s to nearby.
  hijackedTimer = 0;
  hijackedDps = 0;

  // Boss phase tracking
  currentPhase = 1;
  phaseTransitioned = false;

  // Identity for pooled re-use. Incremented on every init() so that
  // long-lived references (e.g. in-flight projectiles) can detect when the
  // slot has been recycled into a different logical enemy.
  version = 0;

  // ── Render-only reaction state (BL-36) ──────────────────────────────────────
  // Drives the EnemyRenderer hit / spawn feedback. Written here and in
  // takeDamage(), decayed by dt in update() — but NEVER read by the sim, so it
  // is determinism-cold: the sim witness captures only active/hp/maxHp/
  // shieldHp/x/y, and these feed no sim quantity. Same pattern as the Tower's
  // render-only fireCooldownVisual.
  /** 1 on damage, decays to 0 — the hit flash + squash/twitch amplitude. */
  hitFlashT = 0;
  /** 0 at spawn, eases to 1 — the spawn scale-in / glow-intro progress. */
  spawnT = 0;

  init(config: EnemyConfig, path: Vec2[], _tileSize: number): this {
    this.version++;
    this.type = config.name;
    this.hp = config.hp;
    this.maxHp = config.hp;
    this.speed = config.speed;
    this.baseSpeed = config.speed;
    this.originalBaseSpeed = config.speed;
    this.reward = config.reward;
    // Colour is injected from the central palette (core/palette.ts) by the
    // DataLoader; fall back to the default if a raw config omits it.
    this.color = config.color ?? this.color;
    this.radius = config.radius;
    this.shape = config.shape ?? "diamond";
    this.archetype = config.archetype ?? "standard";
    this.weakness = config.weakness ?? null;
    this.resistance = config.resistance ?? null;
    this.active = true;
    this.reachedEnd = false;
    this.special = config.special ?? null;
    this.addSpawned = false;
    this.livesDamage = config.livesDamage ?? 1;
    this.stealth = config.stealth ?? false;
    this.revealed = false;
    this.revealedAt = 0;
    this.firewallDamage = config.firewallDamage ?? 0;
    this.immunities = config.immunities ?? [];
    this.shieldHp = 0;
    this.shieldMax = 0;
    this.auraBuffTimer = 0;
    this.auraDamageMult = AURA.DAMAGE_TAKEN_MULT;
    this.shiftTimer = 0;
    this.leechTimer = 0;
    this.leechDrained = 0;

    this.path = [...path];
    this.pathIndex = 0;
    this.stalledMs = 0;
    this.lastProgressIndex = -1;
    this.lastProgressX = Number.NaN;
    this.lastProgressY = Number.NaN;
    // spawnPathId is set by caller (WaveManager.spawnEnemy) AFTER init —
    // it knows which registry path the enemy is being placed on. Default
    // here is "primary" for safety if caller forgets.
    this.spawnPathId = "primary";

    if (path.length > 0) {
      const start = path[0]!;
      this.x = start.x;
      this.y = start.y;
      this.pathIndex = 1;
    }

    this.dots.clear();
    this.slowMultiplier = 1;
    this.slowTimer = 0;
    this.marked = false;
    this.markTimer = 0;
    this.markDamageBonus = 0;
    this.hijackedTimer = 0;
    this.hijackedDps = 0;
    this.currentPhase = 1;
    this.phaseTransitioned = false;
    this.visitedTiles.clear();
    this.pathSpeedBonus = 0;
    // Render-only reaction state: fresh hit flash off, spawn intro begins at 0.
    this.hitFlashT = 0;
    this.spawnT = 0;

    return this;
  }

  reset(): void {
    this.active = false;
    this.path = null;
    this.special = null;
    this.dots.clear();
    this.slowMultiplier = 1;
    this.slowTimer = 0;
    this.marked = false;
    this.markTimer = 0;
    this.markDamageBonus = 0;
    this.hijackedTimer = 0;
    this.hijackedDps = 0;
    this.addSpawned = false;
    // BL-171: the shifter's resistance-cycle timer — re-zeroed here too so
    // reset() honours the pool reset-completeness invariant (below). init() also
    // sets it before reactivation, so this is byte-identical (the value is 0 at
    // every read site); the line closes the one field reset() had been missing.
    this.shiftTimer = 0;
    this.leechTimer = 0;
    this.leechDrained = 0;
    this.currentPhase = 1;
    this.phaseTransitioned = false;
    this.visitedTiles.clear();
    this.pathSpeedBonus = 0;
    // F10: clear P14 fields so pool-recycled slots don't leak boss/stealth
    // flags into the next spawn. init() also sets these, but reset-completeness
    // is the invariant the pool contract expects.
    this.livesDamage = 1;
    this.stealth = false;
    this.revealed = false;
    this.revealedAt = 0;
    this.firewallDamage = 0;
    this.shieldHp = 0;
    this.shieldMax = 0;
    this.auraBuffTimer = 0;
    this.auraDamageMult = AURA.DAMAGE_TAKEN_MULT;
    this.shape = "diamond";
    this.hitFlashT = 0;
    this.spawnT = 0;
  }

  update(dt: number): void {
    if (!this.active) return;
    // Render-only reaction timers (see field docs) — decoupled from sim logic,
    // never read by it. Decaying here (vs. the renderer) means they freeze
    // during hit-stop and slow with slow-mo, matching the rest of the world.
    if (this.hitFlashT > 0) {
      this.hitFlashT = Math.max(0, this.hitFlashT - dt / HIT_FLASH_MS);
    }
    if (this.spawnT < 1) {
      this.spawnT = Math.min(1, this.spawnT + dt / SPAWN_MS);
    }
    this.updateStatusEffects(dt);
    if (!this.active) return;
    this.updateSpecials(dt);
    this.moveAlongPath(dt);
  }

  takeDamage(
    amount: number,
    element?: Element | string,
    opts?: { ignoreResistance?: boolean },
  ): boolean {
    let finalDamage = amount;
    if (this.weakness && element === this.weakness) finalDamage *= 1.5;
    if (
      !opts?.ignoreResistance &&
      this.resistance &&
      element === this.resistance
    ) {
      finalDamage *= 0.5;
    }
    this.applyDamage(finalDamage, element);
    // Render-only: every hit is felt (§6.8) — fire the flash/squash reaction.
    // If this hit is lethal the slot deactivates and the renderer plays the
    // death-pop instead, so the flash simply never draws.
    this.hitFlashT = 1;
    return this.hp <= 0;
  }

  /** Single chokepoint for all damage absorption (shield → HP). */
  private applyDamage(dmg: number, element?: Element | string): void {
    // BL-152 (S154) — the Overseer aura: a buffed enemy (within a living host's
    // field) takes reduced damage, applied at the ONE damage chokepoint so direct
    // hits AND DoT ticks are both mitigated. GUARDED: an unbuffed enemy
    // (auraBuffTimer 0 — every enemy until an Overseer exists in Act III) keeps
    // `dmg` untouched → byte-identical. Bounded (binary, never stacks).
    // S156: the mitigation strength is the STAMPED `auraDamageMult` (lifted to data).
    if (this.auraBuffTimer > 0) dmg *= this.auraDamageMult;
    if (this.shieldMax > 0 && this.shieldHp > 0) {
      const mult = element === "lightning" ? SHIELD.EMP_MULT : 1;
      const shieldDmg = dmg * mult;
      if (shieldDmg < this.shieldHp) {
        this.shieldHp -= shieldDmg;
        return; // fully absorbed
      }
      const baseConsumed = this.shieldHp / mult;
      this.shieldHp = 0;
      dmg -= baseConsumed; // overflow carries to HP at base rate
    }
    this.hp -= dmg;
  }

  applyDot(
    damage: number,
    duration: number,
    tickInterval: number,
    element: Element,
    generation = 0,
    contagion?: ContagionState,
  ): void {
    const state: DotState = {
      damage,
      timer: duration,
      tickTimer: 0,
      tickInterval,
      generation,
    };
    if (contagion !== undefined) state.contagion = contagion;
    this.dots.set(element, state);
  }

  applySlow(multiplier: number, duration: number): void {
    this.slowMultiplier = multiplier;
    this.slowTimer = duration;
  }

  /** Insert a temporary waypoint (honeypot detour) before current path target. */
  insertDetour(waypoint: Vec2): void {
    if (!this.path) return;
    this.path.splice(this.pathIndex, 0, waypoint);
  }

  private updateStatusEffects(dt: number): void {
    // BL-152 (S154): the Overseer aura buff decays unless a living host re-stamps
    // it (in the registry's `overseer` onPostUpdate, AFTER this update). Guarded
    // so an unbuffed enemy (auraBuffTimer 0 — all of Act I–II) is byte-identical.
    if (this.auraBuffTimer > 0) {
      this.auraBuffTimer = Math.max(0, this.auraBuffTimer - dt);
    }

    // Mark decay
    if (this.markTimer > 0) {
      this.markTimer -= dt;
      if (this.markTimer <= 0) {
        this.marked = false;
        this.markDamageBonus = 0;
      }
    }

    // PID_HIJACK: countdown. Crash (die) when timer expires.
    if (this.hijackedTimer > 0) {
      this.hijackedTimer -= dt;
      if (this.hijackedTimer <= 0) {
        this.hijackedTimer = 0;
        this.hijackedDps = 0;
        this.active = false;
        this.reachedEnd = false;
        return;
      }
    }

    // DoT
    for (const [element, dot] of this.dots) {
      dot.timer -= dt;
      dot.tickTimer -= dt;

      if (dot.tickTimer <= 0) {
        let dotDmg = dot.damage;
        if (this.resistance === element) dotDmg *= 0.5;
        if (this.weakness === element) dotDmg *= 1.5;
        this.applyDamage(dotDmg, element);
        dot.tickTimer = dot.tickInterval;
        if (this.hp <= 0) {
          this.active = false;
          this.reachedEnd = false;
          break;
        }
      }

      if (dot.timer <= 0) {
        this.dots.delete(element);
      }
    }

    // Slow (Signal) + virus speed-debuff compose multiplicatively
    let speedMult = this.slowTimer > 0 ? this.slowMultiplier : 1;
    if (this.slowTimer > 0) this.slowTimer -= dt;
    const virusDot = this.dots.get("virus");
    if (virusDot?.contagion) speedMult *= virusDot.contagion.speedDebuff;
    this.speed = this.baseSpeed * speedMult;
  }

  checkPhaseTransition(): number | null {
    if (this.special?.type !== "finalBoss") return null;
    const thresholds = this.special.phaseThresholds as number[] | undefined;
    if (!thresholds || thresholds.length < 2) return null;
    const ratio = this.hp / this.maxHp;
    if (this.currentPhase === 1 && ratio <= thresholds[0]!) return 2;
    if (this.currentPhase === 2 && ratio <= thresholds[1]!) return 3;
    return null;
  }

  private updateSpecials(dt: number): void {
    if (!this.special) return;
    // BL-151: the per-enemy special tick is now an O(1) ENEMY_BEHAVIORS lookup.
    // Only `shiftResistance` registers an `onTick`; the others are no-ops here.
    // Exact relocation — same timer, same shift table, same recolour.
    ENEMY_BEHAVIORS[this.special.type].onTick?.(this, dt);
  }

  private moveAlongPath(dt: number): void {
    if (!this.path || this.pathIndex >= this.path.length) {
      this.reachedEnd = true;
      this.active = false;
      return;
    }

    // PID_HIJACK: move backward along path. Crash when reaching spawn (idx 0).
    if (this.hijackedTimer > 0) {
      if (this.pathIndex <= 0) {
        this.active = false;
        this.reachedEnd = false;
        this.hijackedTimer = 0;
        this.hijackedDps = 0;
        return;
      }
      const effectiveSpeed = this.speed * (1 + this.pathSpeedBonus);
      let remaining = effectiveSpeed * (dt / 1000);
      while (remaining > 0 && this.path && this.pathIndex > 0) {
        const target = this.path[this.pathIndex - 1]!;
        const dx = target.x - this.x;
        const dy = target.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= remaining) {
          remaining -= dist;
          this.x = target.x;
          this.y = target.y;
          this.pathIndex--;
        } else {
          this.x += (dx / dist) * remaining;
          this.y += (dy / dist) * remaining;
          remaining = 0;
        }
      }
      if (this.pathIndex <= 0) {
        this.active = false;
        this.reachedEnd = false;
        this.hijackedTimer = 0;
        this.hijackedDps = 0;
      }
      return;
    }

    const effectiveSpeed = this.speed * (1 + this.pathSpeedBonus);
    let remaining = effectiveSpeed * (dt / 1000);

    while (remaining > 0 && this.path && this.pathIndex < this.path.length) {
      const target = this.path[this.pathIndex]!;
      const dx = target.x - this.x;
      const dy = target.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= remaining) {
        remaining -= dist;
        this.x = target.x;
        this.y = target.y;
        this.pathIndex++;

        const tileKey = `${Math.floor(target.x / 48)},${Math.floor(target.y / 48)}`;
        if (this.visitedTiles.has(tileKey)) {
          this.pathSpeedBonus = Math.min(this.pathSpeedBonus + 0.05, 0.5);
        } else {
          this.visitedTiles.add(tileKey);
        }
      } else {
        this.x += (dx / dist) * remaining;
        this.y += (dy / dist) * remaining;
        remaining = 0;
      }
    }

    if (this.path && this.pathIndex >= this.path.length) {
      this.reachedEnd = true;
      this.active = false;
    }
  }
}
